import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, AlertTriangleIcon } from 'lucide-react';
import { Container } from '../ui/Container';
import { SectionHeading } from '../ui/SectionHeading';
const pathways = [{
  audience: 'Business owners',
  image: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=800&q=80',
  context: "You are focused on running the business — not on legal risk you can't see yet.",
  scope: 'Contracts, disputes, staff, succession, business sales and shareholder issues.',
  risk: 'Signing or agreeing to terms without understanding the commercial exposure.',
  slug: 'commercial-business-law'
}, {
  audience: 'Families',
  image: 'https://images.unsplash.com/photo-1543269865-cbf427effbad?auto=format&fit=crop&w=800&q=80',
  context: 'This is a stressful, personal time and you want to protect what matters most.',
  scope: 'Separation, parenting, property settlement, child support and financial agreements.',
  risk: "Making informal agreements that aren't protected or enforceable later.",
  slug: 'family-law'
}, {
  audience: 'Property buyers, sellers & developers',
  image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=800&q=80',
  context: "A property decision is one of the largest you'll make — the detail matters.",
  scope: 'Settlement, contract review, commercial property, leasing and property disputes.',
  risk: 'Missing a condition or clause that becomes costly after settlement.',
  slug: 'property-law'
}, {
  audience: 'Executors, families & estate planners',
  image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=800&q=80',
  context: "You want to plan properly, or carry out someone's wishes with confidence.",
  scope: 'Wills, EPA, EPG, estate planning, probate and estate disputes.',
  risk: 'An out-of-date or unclear estate plan that creates disputes and delay.',
  slug: 'wills-estates'
}];
export function AudiencePathways() {
  return <section className="bg-cream py-16 lg:py-24" data-id="element-61">
      <Container data-id="element-62">
        <SectionHeading align="center" eyebrow="Support by situation" title="Legal support for the moment you are in." intro="Wherever you're starting from, there's a clear path forward. Find the one that fits your situation." data-id="element-63" />

        <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2" data-id="element-64">
          {pathways.map((p, i) => <motion.article key={p.audience} initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-40px'
        }} transition={{
          duration: 0.35,
          delay: i % 2 * 0.08
        }} className="group overflow-hidden rounded-2xl border border-navy/10 bg-white shadow-card" data-id="element-65">
              <div className="relative h-40 overflow-hidden" data-id="element-66">
                <img src={p.image} alt={p.audience} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-67" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/70 to-transparent" data-id="element-68" />
                <h3 className="absolute bottom-4 left-5 font-serif text-xl text-cream" data-id="element-69">
                  {p.audience}
                </h3>
              </div>
              <div className="p-6" data-id="element-70">
                <p className="text-sm leading-relaxed text-ink/70" data-id="element-71">
                  {p.context}
                </p>
                <p className="mt-3 text-sm text-ink/60" data-id="element-72">
                  <span className="font-semibold text-navy" data-id="element-73">
                    We help with:{' '}
                  </span>
                  {p.scope}
                </p>
                <p className="mt-3 flex items-start gap-2 rounded-lg bg-brass/[0.07] p-3 text-xs leading-relaxed text-ink/70" data-id="element-74">
                  <AlertTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-brass-dark" data-id="element-75" />
                  <span data-id="element-76">
                    <span className="font-semibold text-brass-dark" data-id="element-77">
                      Risk to avoid:{' '}
                    </span>
                    {p.risk}
                  </span>
                </p>
                <Link to={`/services/${p.slug}`} className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-navy transition-colors hover:text-brass-dark" data-id="element-78">
                  See how we help
                  <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" data-id="element-79" />
                </Link>
              </div>
            </motion.article>)}
        </div>
      </Container>
    </section>;
}